<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { usePreferencesStore } from '@/stores/preferences'
import Icon from '@/components/icons/Icon.vue'

const app = useAppStore()
const preferences = usePreferencesStore()

function toggleTheme() {
  const next =
    preferences.theme === 'dark' ? 'light' : preferences.theme === 'light' ? 'system' : 'dark'
  preferences.setTheme(next)
}

const themeIcon = computed(() => {
  if (preferences.theme === 'dark') return 'moon'
  if (preferences.theme === 'light') return 'sun'
  return 'sun'
})
</script>

<template>
  <header class="header">
    <div class="container header__inner">
      <RouterLink to="/" class="header__brand">
        <span class="header__logo" aria-hidden="true">
          <Icon name="calculator" :size="22" />
        </span>
        <span class="header__title">{{ app.siteName }}</span>
      </RouterLink>
      <nav aria-label="Primary" class="header__nav">
        <ul class="header__links">
          <li><RouterLink to="/calculators">Calculators</RouterLink></li>
          <li><RouterLink to="/about">About</RouterLink></li>
          <li><RouterLink to="/methodology">Methodology</RouterLink></li>
        </ul>
        <button
          class="header__theme"
          type="button"
          aria-label="Toggle theme"
          :title="`Theme: ${preferences.theme}`"
          @click="toggleTheme"
        >
          <Icon :name="themeIcon" :size="18" />
        </button>
      </nav>
    </div>
  </header>
</template>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 50;
  height: var(--header-height);
  background-color: rgb(255 255 255 / 0.85);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-border);
  box-shadow: var(--shadow-sm);
}

[data-theme='dark'] .header {
  background-color: rgb(15 23 42 / 0.85);
}

.header__inner {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.header__brand {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-weight: var(--font-bold);
  font-size: var(--text-xl);
  color: var(--color-text);
  text-decoration: none;
}

.header__brand:hover {
  text-decoration: none;
}

.header__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: var(--radius-md);
  background-color: var(--color-primary);
  color: white;
}

.header__title {
  display: none;
}

@media (min-width: 480px) {
  .header__title {
    display: inline;
  }
}

.header__nav {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.header__links {
  display: flex;
  gap: var(--space-3);
  list-style: none;
  margin: 0;
  padding: 0;
}

.header__links a {
  color: var(--color-text-muted);
  font-weight: var(--font-medium);
  font-size: var(--text-sm);
  text-decoration: none;
  padding: var(--space-1) 0;
  border-bottom: 2px solid transparent;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;
}

.header__links a:hover,
.header__links a.router-link-active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary-light);
}

.header__theme {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    transform 0.05s ease;
}

@media (min-width: 480px) {
  .header__links {
    gap: var(--space-4);
  }
}

.header__theme:hover {
  background-color: var(--color-surface-raised);
  border-color: var(--color-primary-light);
  transform: translateY(-1px);
}
</style>
